import { html, LitElement } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { styleMap } from "lit/directives/style-map.js";
import "@shoelace-style/shoelace/dist/components/dropdown/dropdown.js";
import "@shoelace-style/shoelace/dist/components/menu-item/menu-item.js";
import "@shoelace-style/shoelace/dist/components/menu/menu.js";
import styles from "./Autocomplete.styles";
import type SlInput from "@shoelace-style/shoelace/dist/components/input/input.js";
import type SlMenuItem from "@shoelace-style/shoelace/dist/components/menu-item/menu-item.js";
import type SlMenu from "@shoelace-style/shoelace/dist/components/menu/menu.js";
import type { CSSResultGroup } from "lit";

/**
 * @summary Autocomplete displays suggestions as you type.
 *
 * @since unreleased
 * @status unknown
 *
 * @dependency sl-dropdown
 * @dependency sl-menu
 *
 * @slot trigger - The content that includes an input.
 
 * @csspart base - The component's internal wrapper.
 * @csspart trigger - The wrapper for the trigger slot.
 *
 */

@customElement("ds-autocomplete")
export default class Autocomplete extends LitElement {
    static override styles: CSSResultGroup = styles;

    @query("sl-menu") menu?: SlMenu;
    @query("slot:not([name])") defaultSlot?: HTMLSlotElement;

    @state() private value = "";
    @state() protected _dropboxOpen = false;
    @state() protected _haveItems = false;

    _handleSlInput(event: CustomEvent) {
        const { value } = event.target as SlInput;
        this.value = value;

        if (this.value && this.value.length > 0) {
            this._showDropbox();
        } else {
            this._hideDropbox();
        }
    }

    _handleKeydown(event: KeyboardEvent) {
        if (!this._dropboxOpen || event.ctrlKey || event.metaKey) {
            return;
        }

        const options = this.menuItems;

        if (options.length === 0) {
            return;
        }

        const firstItem = options[0];
        const lastItem = options[options.length - 1];

        switch (event.key) {
            case "Tab":
            case "Escape":
                this._hideDropbox();
                break;

            case "ArrowDown":
                event.preventDefault();
                this.menu?.setCurrentItem(firstItem);
                firstItem.focus();
                break;

            case "ArrowUp":
                event.preventDefault();
                this.menu?.setCurrentItem(lastItem);
                lastItem.focus();
                break;
        }
    }

    _handleSlFocus() {
        if (this.value.length > 0) {
            this._showDropbox();
        }
    }

    _handleDropboxAfterHide() {
        this._hideDropbox();
    }

    _showDropbox() {
        this._dropboxOpen = true;
    }

    _hideDropbox() {
        this._dropboxOpen = false;
    }

    get menuItems(): SlMenuItem[] {
        return (this.defaultSlot?.assignedElements() || []) as SlMenuItem[];
    }

    private _menuItemsChanged() {
        this._haveItems = this.menuItems.length > 0;
        this._showDropbox();
    }

    override render() {
        return html`
            <div part="base">
                <div
                    part="trigger"
                    @sl-focus=${this._handleSlFocus}
                    @sl-input=${this._handleSlInput}
                    @keydown=${this._handleKeydown}
                >
                    <slot name="trigger"></slot>
                </div>

                <sl-dropdown
                    ?open=${this._dropboxOpen && this._haveItems}
                    @sl-after-hide=${this._handleDropboxAfterHide}
                >
                    <sl-menu>
                        <slot @slotchange=${() => this._menuItemsChanged()}> </slot>

                        <div
                            aria-hidden="true"
                            style=${styleMap({ width: `${this.clientWidth}px` })}
                        ></div>
                    </sl-menu>
                </sl-dropdown>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-autocomplete": Autocomplete;
    }
}
